// Case Approvals — super_admin / admin_licensing / admin_rm review pending
// LIC + RM case submissions, generate quotes, and approve or deny.
//
// The detail panel, contract/payment sections, tier picker, and the Fix 21
// "View all" review modal live in components/admin/caseApprovals/. This file
// is just the list shell: stat strip, filters, rows, and the expandable panel.

import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { useAuth } from "../../../lib/authContext";
import {
  countPendingApprovals,
  countRecentlyDecided,
  listCasesInBilling,
  listPendingApprovals,
  listPricingTiers,
  type PendingCase,
} from "../../../lib/caseApprovals";
import type { PricingTier } from "../../../types/database";
import { useAdminListFilter } from "../../../lib/useAdminListFilter";
import { listCasesAwaitingFinalization } from "../../../lib/finalInvoice";
import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import {
  AdminButton,
  AdminEmpty,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  FilterChip,
} from "../../../components/admin/ui";
import { StatStrip } from "../../../components/aspects/StatStrip";
import { StatusPill } from "../../../components/aspects/StatusPill";
import { SectionHeader } from "../../../components/aspects/SectionHeader";
import { CaseDetailPanel } from "../../../components/admin/caseApprovals/CaseDetailPanel";
import { caseStatusTone, engagementLabel, formatRelative } from "../../../components/admin/caseApprovals/helpers";

const THEME = PANE_THEMES.Admin;

type EngagementFilter = "all" | "licensing" | "rm" | "rm_admin";
const ENGAGEMENT_FILTERS: { key: EngagementFilter; label: string }[] = [
  { key: "all", label: "All engagements" },
  { key: "licensing", label: "LIC self-submit" },
  { key: "rm", label: "Coravu RM" },
  { key: "rm_admin", label: "Coravu-Managed (admin)" },
];

export default function AdminCaseApprovalsScreen() {
  const { role } = useAuth();
  const isSuperAdmin = role === "super_admin";

  const [rows, setRows] = useState<PendingCase[]>([]);
  const [billingRows, setBillingRows] = useState<PendingCase[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [decidedCount, setDecidedCount] = useState(0);
  const [pricing, setPricing] = useState<PricingTier[]>([]);
  const [awaitingFinal, setAwaitingFinal] = useState<
    Array<{ id: string; name: string; company_name: string | null; days_overdue: number }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const filterUrl = useAdminListFilter({
    keys: ["engagement", "case"],
    defaults: { engagement: "all", case: "" },
  });
  const engagementFilter = filterUrl.get("engagement") as EngagementFilter;
  const openCaseId = filterUrl.get("case") || null;

  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const [list, billing, pCount, dCount, pricingRows, awaiting] = await Promise.all([
        listPendingApprovals(),
        listCasesInBilling(),
        countPendingApprovals(),
        countRecentlyDecided(),
        pricing.length > 0 ? Promise.resolve(pricing) : listPricingTiers(),
        listCasesAwaitingFinalization(),
      ]);
      setRows(list);
      setBillingRows(billing);
      setPendingCount(pCount);
      setDecidedCount(dCount);
      setAwaitingFinal(
        awaiting.map((c) => ({ id: c.id, name: c.name, company_name: c.company_name, days_overdue: c.days_overdue })),
      );
      if (pricing.length === 0) setPricing(pricingRows);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
    // pricing.length stays stable after first fetch — intentional dep choice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const rowFilter = useCallback(
    (r: PendingCase) => {
      if (engagementFilter === "rm_admin") {
        if (r.engagement_model !== "rm" || !r.created_by_admin) return false;
      } else if (engagementFilter === "licensing" && r.engagement_model !== "licensing") {
        return false;
      } else if (engagementFilter === "rm" && r.engagement_model !== "rm") {
        return false;
      }
      if (role === "admin_licensing" && r.engagement_model !== "licensing") return false;
      if (role === "admin_rm" && r.engagement_model !== "rm") return false;
      return true;
    },
    [engagementFilter, role],
  );

  const visibleRows = useMemo(() => rows.filter(rowFilter), [rows, rowFilter]);
  const visibleBillingRows = useMemo(() => billingRows.filter(rowFilter), [billingRows, rowFilter]);

  const renderCaseRow = (row: PendingCase) => {
    const expanded = row.id === openCaseId;
    return (
      <View key={row.id}>
        <Pressable
          onPress={() => filterUrl.set({ case: expanded ? "" : row.id })}
          style={[
            {
              padding: 14,
              borderRadius: RADII.md,
              backgroundColor: THEME.surfaceRaisedBg,
              borderWidth: STROKES.hairline,
              borderColor: expanded ? THEME.accentBorderHover : THEME.border,
            } as ViewStyle,
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
            <StatusPill theme={THEME} label={engagementLabel(row.engagement_model)} tone="grey" showDot={false} />
            <StatusPill theme={THEME} label={row.tier ? `Tier ${row.tier}` : "No tier"} tone="grey" showDot={false} />
            {row.created_by_admin ? <StatusPill theme={THEME} label="Coravu-Managed" tone="amber" showDot={false} /> : null}
            <StatusPill theme={THEME} label={row.status} tone={caseStatusTone(row.status)} />
            <View style={{ flex: 1 }} />
            <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>
              Submitted {formatRelative(row.submitted_at)}
            </Text>
          </View>
          <Text style={{ marginTop: 10, fontFamily: THEME.bodyMediumFont, fontSize: 14, color: THEME.textOnSurface }}>
            {row.name}
          </Text>
          <Text style={{ marginTop: 1, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>
            {row.company_name ?? "Unknown company"}
            {row.client_full_name ? ` · ${row.client_full_name}` : ""}
          </Text>
          {row.created_by_admin ? (
            <Text style={{ marginTop: 4, fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.accent }}>
              🏛️ Coravu-Managed{row.admin_creator_name ? ` by ${row.admin_creator_name}` : ""}
            </Text>
          ) : null}
        </Pressable>

        {expanded ? (
          <CaseDetailPanel
            key={`detail-${row.id}`}
            competitionId={row.id}
            isSuperAdmin={isSuperAdmin}
            pricing={pricing}
            onClose={() => filterUrl.set({ case: "" })}
            onActionComplete={() => {
              filterUrl.set({ case: "" });
              void load();
            }}
            onDecided={() => void load()}
          />
        ) : null}
      </View>
    );
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 64 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Inbox · Case Approvals"
          title="Case approvals"
          subtitle={`Review submitted cases, generate quotes, and approve or deny. ${
            role === "admin_licensing"
              ? "You see Licensing cases."
              : role === "admin_rm"
                ? "You see Coravu RM cases."
                : "You see all cases."
          }`}
        />

        {err ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={err} onRetry={() => void load()} /></View> : null}

        {/* Stat strip */}
        <StatStrip
          theme={THEME}
          eyebrow="Inbox"
          cells={[
            { label: "Pending review", value: pendingCount, color: THEME.accent },
            { label: "Recently decided · 7d", value: decidedCount },
          ]}
        />

        {/* Awaiting-finalization banner */}
        {awaitingFinal.length > 0 ? (
          <View
            style={{
              marginTop: SPACING.md,
              padding: 16,
              borderRadius: RADII.md,
              borderWidth: STROKES.hairline,
              borderColor: THEME.accentBorderHover,
              backgroundColor: THEME.accentSoft,
              gap: 10,
            }}
          >
            <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 10, letterSpacing: 1.8, textTransform: "uppercase", color: THEME.accent }}>
              {awaitingFinal.length} case{awaitingFinal.length === 1 ? "" : "s"} ready to close out
            </Text>
            <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12.5, lineHeight: 18, color: THEME.textOnSurfaceDim }}>
              These cases ran more than 7 days ago and haven&apos;t been finalized. Click Complete to enter final counts and generate the closeout invoice.
            </Text>
            <View style={{ gap: 6 }}>
              {awaitingFinal.slice(0, 5).map((c) => (
                <View
                  key={c.id}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                    padding: 10,
                    borderRadius: RADII.sm,
                    backgroundColor: THEME.surfaceBg,
                    borderWidth: STROKES.hairline,
                    borderColor: THEME.border,
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 12.5, color: THEME.textOnSurface }}>{c.name}</Text>
                    <Text style={{ marginTop: 2, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>
                      {c.company_name ?? "—"} · closed {c.days_overdue}d ago
                    </Text>
                  </View>
                  <AdminButton theme={THEME} label="Complete →" size="sm" onPress={() => router.push(`/admin/cases/${c.id}/complete` as Href)} />
                </View>
              ))}
            </View>
          </View>
        ) : null}

        {/* Engagement filter */}
        <View style={{ marginTop: SPACING.lg }}>
          <SectionHeader theme={THEME} title="Engagement" tone="dark" />
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
            {ENGAGEMENT_FILTERS.map((f) => (
              <FilterChip key={f.key} theme={THEME} active={engagementFilter === f.key} label={f.label} onPress={() => filterUrl.set({ engagement: f.key })} />
            ))}
          </View>
        </View>

        {/* List */}
        <View style={{ marginTop: SPACING.md }}>
          {loading && rows.length === 0 ? (
            <AdminLoading theme={THEME} label="Loading cases…" />
          ) : visibleRows.length === 0 ? (
            <AdminEmpty
              theme={THEME}
              title="No cases pending review."
              body="When a client submits a case draft you'll see it here."
            />
          ) : (
            <View style={{ gap: 10 }}>{visibleRows.map(renderCaseRow)}</View>
          )}
        </View>

        {/* In billing */}
        {visibleBillingRows.length > 0 ? (
          <View style={{ marginTop: SPACING.xl }}>
            <SectionHeader theme={THEME} title="In billing — contract & payment" tone="dark" />
            <View style={{ gap: 10 }}>{visibleBillingRows.map(renderCaseRow)}</View>
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
}
